
import React from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useProject } from "@/contexts/ProjectContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Plus, Filter, SortDesc } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export default function RisksPage() {
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const isMobile = useIsMobile();

  if (!currentProject) {
    navigate("/projects-selector");
    return null;
  }

  // Risk score calculation
  const calculateRiskScore = (probability: number, impact: number) => probability * impact;

  // Risk severity classification
  const getRiskSeverity = (score: number) => {
    if (score >= 16) return { label: "Critical", className: "text-red-600" };
    if (score >= 10) return { label: "High", className: "text-orange-600" };
    if (score >= 4) return { label: "Medium", className: "text-amber-600" };
    return { label: "Low", className: "text-green-600" };
  };

  // Simplified card display for mobile
  const RiskCard = ({ risk }: { risk: any }) => {
    const score = calculateRiskScore(risk.probability, risk.impact);
    const severity = getRiskSeverity(score);
    
    return (
      <Card className="mb-4 cursor-pointer hover:shadow-md" onClick={() => navigate(`/risks/${risk.id}`)}>
        <CardContent className="pt-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-medium">{risk.name}</h3>
              <p className="text-sm text-muted-foreground">Stage {risk.stage} • {risk.owner}</p>
            </div>
            <span className={`px-2 py-1 text-xs rounded-full ${
              risk.status === 'open' ? 'bg-amber-100 text-amber-800' :
              risk.status === 'mitigated' ? 'bg-blue-100 text-blue-800' : 
              'bg-green-100 text-green-800'
            }`}>
              {risk.status}
            </span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <div className={`text-sm font-medium ${severity.className}`}>
              {severity.label} ({score})
            </div>
            <div className="text-sm text-muted-foreground">
              R-{risk.id}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      
      <main className="container py-6 flex-1">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Risk Register</h1>
            <p className="text-muted-foreground">
              {currentProject.name} • {currentProject.risks.filter(r => r.status === "open").length} open risks
            </p>
          </div>
          <Button onClick={() => navigate("/risks/new")} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" /> New Risk
          </Button>
        </div>
        
        {!isMobile && (
          <div className="flex items-center gap-2 mb-4">
            <Button variant="outline" size="sm">
              <Filter className="mr-2 h-4 w-4" /> Filter
            </Button>
            <Button variant="outline" size="sm">
              <SortDesc className="mr-2 h-4 w-4" /> Sort
            </Button>
          </div>
        )}
        
        {isMobile ? (
          // Mobile view - cards instead of table
          <div>
            {currentProject.risks.length > 0 ? (
              currentProject.risks.map((risk) => (
                <RiskCard key={risk.id} risk={risk} />
              ))
            ) : (
              <Card className="text-center py-6">
                <CardContent>
                  <AlertTriangle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="mb-4">No risks found for this project</p>
                  <Button variant="outline" size="sm" onClick={() => navigate("/risks/new")}>
                    Add your first risk
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          // Desktop view - table layout
          <Card>
            <CardHeader className="pb-0">
              <CardTitle>All Risks</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Risk Name</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentProject.risks.length > 0 ? (
                    currentProject.risks.map((risk) => {
                      const score = calculateRiskScore(risk.probability, risk.impact);
                      const severity = getRiskSeverity(score);
                      
                      return (
                        <TableRow 
                          key={risk.id} 
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/risks/${risk.id}`)}
                        >
                          <TableCell>R-{risk.id}</TableCell>
                          <TableCell>{risk.name}</TableCell>
                          <TableCell>Stage {risk.stage}</TableCell>
                          <TableCell>{risk.owner}</TableCell>
                          <TableCell>
                            <span className={severity.className}>
                              {severity.label} ({score})
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              risk.status === 'open' ? 'bg-amber-100 text-amber-800' :
                              risk.status === 'mitigated' ? 'bg-blue-100 text-blue-800' : 
                              'bg-green-100 text-green-800'
                            }`}>
                              {risk.status}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6">
                        <div className="flex flex-col items-center gap-2">
                          <AlertTriangle className="h-8 w-8 text-muted-foreground" />
                          <p>No risks found for this project</p>
                          <Button variant="outline" size="sm" onClick={() => navigate("/risks/new")}>
                            Add your first risk
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
